import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Checkbox, Modal, Popconfirm, Spin } from 'antd';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import GenericTable from '../../../../components/tables/GenericTable';
import Pagination from '../../../../layout/Pagination';
import {
  fetchTherapyOccurrences,
  fetchTherapiesPerUserHistory,
  updateTherapyOccurrenceNotification,
} from '../../api/therapyOccurrencesApi';
import {
  activateTherapyReservation,
  deleteTherapyReservation,
  fetchUserTherapies,
} from '../../api/therapiesApi';
import {
  getHighlightedActivity,
  getOccurrenceNotificationLabel,
  getOccurrenceStatusDisplayLabel,
  shouldShowOccurrenceNotificationDate,
  sortOccurrences,
} from '../../utils/therapyOccurrencePresentation';
import { getTherapyOccurrenceRequestStatusLabel } from '../../../therapyOccurrenceRequests/utils/therapyOccurrenceRequestPresentation';
import UpdateTherapyOccurrenceRequestStatusModal from '../../../therapyOccurrenceRequests/components/modals/UpdateTherapyOccurrenceRequestStatusModal';
import UpdateOccurrenceStatusModal from './UpdateOccurrenceStatusModal';
import OccurrenceStatusLogsModal from './OccurrenceStatusLogsModal';
import UpdateTherapyTemplateModal from './UpdateTherapyTemplateModal';
import RenewTherapyModal from './RenewTherapyModal';
import TerminateTherapyModal from './TerminateTherapyModal';
import TherapyReservationModal from './TherapyReservationModal';
import ViewTherapyReservationModal from './ViewTherapyReservationModal';
import AddTherapyModal from '../../../users/components/modals/AddTherapyModal';
import useToast from '../../../../shared/hooks/useToast';
import StatusFlag from '../../../../components/elements/StatusFlag';
import {
  STATUS_TONES,
  getNotificationStatusTone,
  getOccurrenceStatusTone,
  getRequestStatusTone,
  getTherapyStatusTone,
} from '../../../../shared/utils/statusTones';

const getTherapyUser = (therapy) =>
  therapy
    ? {
        _id: therapy.userId,
        userName: therapy.userName,
        firstName: therapy.firstName,
        lastName: therapy.lastName,
        email: therapy.userEmail || therapy.email,
        phone: therapy.userPhone || therapy.phone,
      }
    : null;

const getTherapyFromRenewResponse = (data) =>
  data?.therapy ||
  data?.newTherapy ||
  data?.renewedTherapy ||
  data?.data?.therapy ||
  data?.data?.newTherapy ||
  data?.data?.renewedTherapy ||
  data?.data ||
  null;

const getTherapiesFromResponse = (data) =>
  data?.therapies || data?.data?.therapies || [];

const getFirstCurrentRegularTherapy = (therapies, previousTherapyId = null) =>
  (Array.isArray(therapies) ? therapies : []).find(
    (therapy) =>
      therapy?._id !== previousTherapyId &&
      therapy?.isAcute !== true &&
      therapy?.status === 'current'
  ) ||
  (Array.isArray(therapies) ? therapies : []).find(
    (therapy) => therapy?.isAcute !== true && therapy?.status === 'current'
  ) ||
  null;

const actionRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 14,
  marginBottom: 12,
};

const getActionButtonStyle = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  minHeight: 34,
  padding: '0 12px',
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  background: '#fff',
  color: '#111827',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 14,
  fontWeight: 600,
  opacity: disabled ? 0.55 : 1,
});

const therapyInfoStyle = {
  display: 'grid',
  gap: 6,
  marginBottom: 12,
};

const therapyInfoLabelStyle = {
  fontWeight: 600,
};

const internalNoteInfoStyle = {
  backgroundColor: '#ffeb3b',
  border: '1px solid #f3cf00',
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: 6,
};

const reservationChipStyle = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 6,
  backgroundColor: '#e6f4ff',
  border: '1px solid #91caff',
  color: '#0958d9',
  fontWeight: 600,
};

const historyOccurrenceRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const historyOccurrenceViewButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  background: '#fff',
  cursor: 'pointer',
};

const historyOccurrenceActionsStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
};

const historyOccurrenceSavedTextStyle = {
  display: 'inline-block',
  borderRadius: 4,
  background: '#ffccc7',
  boxShadow: '0 0 0 2px #ffccc7',
  color: '#4d4134',
};

const historyAcuteDescriptionStyle = {
  fontStyle: 'italic',
};

const HISTORY_PAGE_SIZE = 10;

const TherapyOccurrencesModal = ({
  isOpen,
  onClose,
  therapy,
  onRefetchTherapies,
  onReloadTherapyContext,
}) => {
  const messageApi = useToast();
  const [occurrences, setOccurrences] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [message, setMessage] = useState('');
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isRequestUpdateOpen, setIsRequestUpdateOpen] = useState(false);
  const [historyTherapies, setHistoryTherapies] = useState([]);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [notificationUpdatingIds, setNotificationUpdatingIds] = useState([]);
  const [isTemplateUpdateOpen, setIsTemplateUpdateOpen] = useState(false);
  const [isRenewOpen, setIsRenewOpen] = useState(false);
  const [isTerminateOpen, setIsTerminateOpen] = useState(false);
  const [isAddNewOpen, setIsAddNewOpen] = useState(false);
  const [isAddAcuteOpen, setIsAddAcuteOpen] = useState(false);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isViewReservationOpen, setIsViewReservationOpen] = useState(false);
  const [modalActionLoading, setModalActionLoading] = useState(false);
  const [currentTherapy, setCurrentTherapy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const latestOccurrencesRequestRef = useRef(0);
  const latestHistoryRequestRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setHistoryCurrentPage(1);
    }
  }, [isOpen, therapy?._id]);

  useEffect(() => {
    setCurrentTherapy(therapy || null);
  }, [therapy]);

  useEffect(() => {
    if (isOpen) return;
    // Po zatvaranju modala obrisi podatke prethodnog pacijenta i ponisti
    // fetch koji je eventualno jos u toku, da se ne prikazu stare aktivnosti.
    latestOccurrencesRequestRef.current += 1;
    latestHistoryRequestRef.current += 1;
    setOccurrences([]);
    setHistoryTherapies([]);
    setTotalPages(1);
    setHistoryTotalPages(1);
    setHistoryTotalCount(0);
    setCurrentPage(1);
    setHistoryCurrentPage(1);
    setMessage('');
    setLoading(false);
    setHistoryLoading(false);
    setIsTemplateUpdateOpen(false);
    setIsRenewOpen(false);
    setIsTerminateOpen(false);
    setIsAddNewOpen(false);
    setIsAddAcuteOpen(false);
    setIsReservationModalOpen(false);
    setIsViewReservationOpen(false);
    setModalActionLoading(false);
  }, [isOpen]);

  const loadOccurrences = useCallback(async () => {
    if (!isOpen || !currentTherapy?._id) return;
    if (currentTherapy?.isAcute) {
      setOccurrences([]);
      return;
    }

    const requestId = latestOccurrencesRequestRef.current + 1;
    latestOccurrencesRequestRef.current = requestId;
    setLoading(true);

    const { data, ok, message } = await fetchTherapyOccurrences({
      therapyId: currentTherapy._id,
      page: currentPage,
      limit: 10,
    });

    // Ignorisi odgovor ako je u medjuvremenu pokrenut noviji zahtev
    // (ili je modal zatvoren), da stari podaci ne pregaze nove.
    if (requestId !== latestOccurrencesRequestRef.current) return;

    if (ok && data) {
      setOccurrences(data.occurrences || []);
      setCurrentPage(data?.pagination?.currentPage || 1);
      setTotalPages(data?.pagination?.totalPages || 1);
    } else if (message) {
      setMessage(message);
    }

    setLoading(false);
  }, [currentPage, currentTherapy?._id, currentTherapy?.isAcute, isOpen]);

  useEffect(() => {
    loadOccurrences();
  }, [loadOccurrences]);

  const loadHistoryTherapiesPerUser = useCallback(async (page = historyCurrentPage) => {
    const userId = currentTherapy?.userId;
    if (!isOpen || !userId) return;

    const requestId = latestHistoryRequestRef.current + 1;
    latestHistoryRequestRef.current = requestId;
    setHistoryLoading(true);

    const { data, ok, message } = await fetchTherapiesPerUserHistory({
      userId,
      page,
      limit: HISTORY_PAGE_SIZE,
    });

    // Ignorisi odgovor ako je pokrenut noviji zahtev ili je modal zatvoren.
    if (requestId !== latestHistoryRequestRef.current) return;

    if (ok && data) {
      setHistoryTherapies(data.therapies || []);
      setHistoryCurrentPage(data?.pagination?.currentPage || 1);
      setHistoryTotalPages(data?.pagination?.totalPages || 1);
      setHistoryTotalCount(data?.pagination?.totalCount || 0);
    } else if (message) {
      setMessage(message);
    }

    setHistoryLoading(false);
  }, [currentTherapy?.userId, historyCurrentPage, isOpen]);

  useEffect(() => {
    loadHistoryTherapiesPerUser();
  }, [loadHistoryTherapiesPerUser]);

  const handleOccurrenceUpdated = useCallback(
    async (updatedOccurrence) => {
      if (updatedOccurrence?._id) {
        setOccurrences((prev) =>
          prev.map((occurrence) =>
            occurrence._id === updatedOccurrence._id
              ? { ...occurrence, ...updatedOccurrence }
              : occurrence
          )
        );

        setSelectedOccurrence((prev) =>
          prev?._id === updatedOccurrence._id
            ? { ...prev, ...updatedOccurrence }
            : prev
        );

        setHistoryTherapies((prev) =>
          prev.map((therapyItem) => ({
            ...therapyItem,
            occurrences: Array.isArray(therapyItem?.occurrences)
              ? therapyItem.occurrences.map((occurrence) =>
                  occurrence?._id === updatedOccurrence._id
                    ? { ...occurrence, ...updatedOccurrence }
                    : occurrence
                )
              : therapyItem?.occurrences,
          }))
        );
      }

      await loadOccurrences();
      const refreshedTherapy = await onReloadTherapyContext?.(currentTherapy);
      if (refreshedTherapy) {
        setCurrentTherapy(refreshedTherapy);
      }
      await loadHistoryTherapiesPerUser();
    },
    [
      currentTherapy,
      loadHistoryTherapiesPerUser,
      loadOccurrences,
      onReloadTherapyContext,
    ]
  );

  const handleRequestUpdated = useCallback(
    async (updatedRequest) => {
      if (updatedRequest?._id) {
        setOccurrences((prev) =>
          prev.map((occurrence) =>
            occurrence?.request?._id === updatedRequest._id
              ? {
                  ...occurrence,
                  request: {
                    ...occurrence.request,
                    ...updatedRequest,
                  },
                }
              : occurrence
          )
        );

        setSelectedRequest((prev) =>
          prev?._id === updatedRequest._id
            ? { ...prev, ...updatedRequest }
            : prev
        );
      }

      await loadOccurrences();
      onRefetchTherapies?.();
    },
    [loadOccurrences, onRefetchTherapies]
  );

  const handleNotificationChange = useCallback(
    async (occurrence, notificationStatus) => {
      if (!occurrence?._id) return;

      setNotificationUpdatingIds((prev) => [
        ...new Set([...prev, occurrence._id]),
      ]);

      try {
        const {
          data,
          ok,
          message: responseMessage,
        } = await updateTherapyOccurrenceNotification(occurrence._id, {
          notificationStatus,
        });

        if (!ok) {
          const errorMessage =
            responseMessage || 'Doslo je do greske pri azuriranju obavestenja.';
          setMessage(errorMessage);
          messageApi.error(errorMessage);
          return;
        }

        const updatedOccurrence =
          data?.occurrence || data?.data?.occurrence || data?.data || null;

        if (updatedOccurrence) {
          setOccurrences((prev) =>
            prev.map((item) =>
              item?._id === updatedOccurrence._id
                ? { ...item, ...updatedOccurrence }
                : item
            )
          );
          onRefetchTherapies?.();
        }

        messageApi.success(responseMessage || 'Obavestenje je azurirano.');
      } finally {
        setNotificationUpdatingIds((prev) =>
          prev.filter((id) => id !== occurrence._id)
        );
      }
    },
    [messageApi, onRefetchTherapies]
  );

  const handleTemplateUpdated = useCallback(
    async (updatedTherapy) => {
      if (updatedTherapy?._id) {
        setCurrentTherapy((prev) => ({ ...prev, ...updatedTherapy }));
      }

      await loadOccurrences();
      await loadHistoryTherapiesPerUser();
      onRefetchTherapies?.();
    },
    [loadHistoryTherapiesPerUser, loadOccurrences, onRefetchTherapies]
  );

  const handleTherapyRenewed = useCallback(
    async (responseData) => {
      setModalActionLoading(true);

      try {
        // Posle obnove osvezavamo pun kontekst terapije preko istog izvora kao
        // pri otvaranju modala (`/therapy-modal-context`), da nova aktuelna
        // terapija ponese `reservation` i `reservationActivation` (npr. unapred
        // rezervacija iz prethodne terapije koja sada treba da se primeni na ovu).
        // Fallback: therapy iz renew odgovora, pa lista aktuelnih terapija.
        let renewedTherapy = await onReloadTherapyContext?.(currentTherapy);

        if (!renewedTherapy?._id) {
          renewedTherapy = getTherapyFromRenewResponse(responseData);
        }

        if (!renewedTherapy?._id && currentTherapy?.userId) {
          const { data, ok } = await fetchUserTherapies({
            userId: currentTherapy.userId,
            page: 1,
            limit: 20,
            status: 'current',
          });

          const currentTherapies = getTherapiesFromResponse(data);

          if (ok && currentTherapies.length) {
            renewedTherapy =
              getFirstCurrentRegularTherapy(
                currentTherapies,
                currentTherapy?._id
              ) ||
              currentTherapies.find((therapy) => therapy?.isAcute !== true) ||
              currentTherapies[0];
          }
        }

        if (renewedTherapy?._id) {
          setCurrentTherapy(renewedTherapy);
          setCurrentPage(1);

          if (renewedTherapy?.isAcute) {
            setOccurrences([]);
            setTotalPages(1);
          } else {
            const { data, ok, message } = await fetchTherapyOccurrences({
              therapyId: renewedTherapy._id,
              page: 1,
              limit: 10,
            });

            if (ok && data) {
              setOccurrences(data.occurrences || []);
              setTotalPages(data?.pagination?.totalPages || 1);
            } else if (message) {
              setMessage(message);
            }
          }
        } else {
          await loadOccurrences();
        }

        await loadHistoryTherapiesPerUser();
        onRefetchTherapies?.();
      } finally {
        setModalActionLoading(false);
      }
    },
    [
      currentTherapy,
      loadHistoryTherapiesPerUser,
      loadOccurrences,
      onReloadTherapyContext,
      onRefetchTherapies,
    ]
  );

  const handleTherapyTerminated = useCallback(
    async (responseData) => {
      const responseMessage =
        responseData?.message || responseData?.data?.message || '';

      if (responseMessage) {
        messageApi.success(responseMessage);
      }

      setCurrentTherapy((prev) =>
        prev
          ? {
              userId: prev.userId,
              userName: prev.userName,
              firstName: prev.firstName,
              lastName: prev.lastName,
              userEmail: prev.userEmail,
              email: prev.email,
              userPhone: prev.userPhone,
              phone: prev.phone,
              userInternalNote: prev.userInternalNote,
              internalNote: prev.internalNote,
            }
          : null
      );
      setOccurrences([]);
      setCurrentPage(1);
      setTotalPages(1);
      await loadHistoryTherapiesPerUser();
      onRefetchTherapies?.();
      setIsAddNewOpen(true);
    },
    [loadHistoryTherapiesPerUser, messageApi, onRefetchTherapies]
  );

  const handleNewTherapyCreated = useCallback(async () => {
    const userId = currentTherapy?.userId;
    const previousTherapyId = currentTherapy?._id || null;

    setIsAddNewOpen(false);
    setModalActionLoading(true);

    try {
      if (userId) {
        const { data, ok } = await fetchUserTherapies({
          userId,
          page: 1,
          limit: 20,
          status: 'current',
        });
        const currentTherapies = getTherapiesFromResponse(data);
        const newCurrentTherapy = ok
          ? getFirstCurrentRegularTherapy(currentTherapies, previousTherapyId)
          : null;

        if (newCurrentTherapy?._id) {
          setCurrentTherapy(newCurrentTherapy);
          setCurrentPage(1);

          const {
            data: occurrencesData,
            ok: occurrencesOk,
            message: occurrencesMessage,
          } = await fetchTherapyOccurrences({
            therapyId: newCurrentTherapy._id,
            page: 1,
            limit: 10,
          });

          if (occurrencesOk && occurrencesData) {
            setOccurrences(occurrencesData.occurrences || []);
            setTotalPages(occurrencesData?.pagination?.totalPages || 1);
          } else if (occurrencesMessage) {
            setMessage(occurrencesMessage);
          }
        }
      }

      await loadHistoryTherapiesPerUser();
      onRefetchTherapies?.();
    } finally {
      setModalActionLoading(false);
    }
  }, [
    currentTherapy?._id,
    currentTherapy?.userId,
    loadHistoryTherapiesPerUser,
    onRefetchTherapies,
  ]);

  const handleAcuteTherapyCreated = useCallback(async () => {
    setIsAddAcuteOpen(false);
    setModalActionLoading(true);

    try {
      setHistoryCurrentPage(1);
      await loadHistoryTherapiesPerUser(1);
      onRefetchTherapies?.();
    } finally {
      setModalActionLoading(false);
    }
  }, [loadHistoryTherapiesPerUser, onRefetchTherapies]);

  const handleReservationChanged = useCallback(
    async (responseData) => {
      setModalActionLoading(true);

      try {
        // Sve rezervacione rute vracaju azuriran TherapyView; ipak, radi
        // konzistentnosti (npr. brisanje uklanja polje reservation) osvezavamo
        // kompletan kontekst, uz fallback na therapy iz odgovora.
        const refreshedTherapy = await onReloadTherapyContext?.(currentTherapy);

        if (refreshedTherapy) {
          setCurrentTherapy(refreshedTherapy);
        } else {
          const updatedTherapy = getTherapyFromRenewResponse(responseData);
          if (updatedTherapy?._id) {
            setCurrentTherapy(updatedTherapy);
          }
        }

        await loadOccurrences();
        await loadHistoryTherapiesPerUser();
        onRefetchTherapies?.();
      } finally {
        setModalActionLoading(false);
      }
    },
    [
      currentTherapy,
      loadHistoryTherapiesPerUser,
      loadOccurrences,
      onReloadTherapyContext,
      onRefetchTherapies,
    ]
  );

  const handleActivateReservation = useCallback(async () => {
    if (!currentTherapy?._id) return;

    setModalActionLoading(true);

    const {
      data,
      ok,
      message: responseMessage,
    } = await activateTherapyReservation(currentTherapy._id);

    if (!ok) {
      messageApi.error(
        responseMessage || 'Doslo je do greske pri aktivaciji rezervacije.'
      );
      setModalActionLoading(false);
      return;
    }

    messageApi.success(responseMessage || 'Rezervacija je aktivirana.');
    await handleReservationChanged(data);
  }, [currentTherapy?._id, handleReservationChanged, messageApi]);

  const handleDeleteReservation = useCallback(async () => {
    if (!currentTherapy?._id) return;

    setModalActionLoading(true);

    const {
      data,
      ok,
      message: responseMessage,
    } = await deleteTherapyReservation(currentTherapy._id);

    if (!ok) {
      messageApi.error(
        responseMessage || 'Doslo je do greske pri brisanju rezervacije.'
      );
      setModalActionLoading(false);
      return;
    }

    messageApi.success(responseMessage || 'Rezervacija je obrisana.');
    await handleReservationChanged(data);
  }, [currentTherapy?._id, handleReservationChanged, messageApi]);

  const handleLogsClose = useCallback(() => {
    setIsLogsOpen(false);
    setSelectedOccurrence(null);
  }, []);

  const columns = useMemo(
    () => [
      { key: 'sequenceNumber', label: 'Mesec', render: (v) => v || '-' },
      {
        key: 'scheduledDate',
        label: 'Centralni datum',
        render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '-'),
      },
      {
        key: 'status',
        label: 'Status',
        render: (_, row) =>
          row?.isRenewRow ? (
            <StatusFlag tone={STATUS_TONES.neutral}>Obnova</StatusFlag>
          ) : (
            <StatusFlag tone={getOccurrenceStatusTone(row?.status)}>
              {getOccurrenceStatusDisplayLabel(row)}
            </StatusFlag>
          ),
      },
      {
        key: 'notificationStatus',
        label: 'Obaveštenje',
        render: (_, row) => {
          if (row?.isRenewRow || row?.status === 'unavailable') return '';

          const notificationDate = shouldShowOccurrenceNotificationDate(row)
            ? dayjs(row.notificationDate).format('DD.MM.YYYY')
            : '';
          const notificationLabel =
            currentTherapy?.hasDeviceToken &&
            row?.notificationStatus === 'needs_contact'
              ? 'Zakazana notif.'
              : getOccurrenceNotificationLabel(row);
          const showContactedAction =
            row?.notificationStatus === 'needs_contact' &&
            !currentTherapy?.hasDeviceToken;

          return (
            <div>
              <StatusFlag
                tone={getNotificationStatusTone(row?.notificationStatus)}
              >
                {notificationLabel}
              </StatusFlag>
              {notificationDate ? <div>{notificationDate}</div> : null}
              {showContactedAction ? (
                <Checkbox
                  checked={false}
                  disabled={notificationUpdatingIds.includes(row._id)}
                  onChange={() => handleNotificationChange(row, 'contacted')}
                >
                  Javljeno
                </Checkbox>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'request',
        label: 'Zahtev',
        render: (_, row) =>
          row?.isRenewRow
            ? ''
            : row?.request
              ? (
                  <>
                    {`${row.request.pickupMethod || '-'} / `}
                    <StatusFlag tone={getRequestStatusTone(row.request.status)}>
                      {getTherapyOccurrenceRequestStatusLabel(row.request.status)}
                    </StatusFlag>
                  </>
                )
              : '-',
      },
      {
        key: 'requestedDeliveryDate',
        label: 'Zeljeni datum dostave',
        render: (_, row) =>
          row?.isRenewRow
            ? ''
            : row?.request?.requestedDeliveryDate
              ? dayjs(row.request.requestedDeliveryDate).format('DD.MM.YYYY')
              : '-',
      },
      {
        key: 'userMessage',
        label: 'Odgovor korisnika',
        render: (v, row) => (row?.isRenewRow ? '' : v || '-'),
      },
      {
        key: 'note',
        label: 'Napomena',
        render: (v, row) => (row?.isRenewRow ? '' : v || '-'),
      },
    ],
    [
      currentTherapy?.hasDeviceToken,
      handleNotificationChange,
      notificationUpdatingIds,
    ]
  );

  const patientName =
    currentTherapy?.userName?.trim() ||
    `${currentTherapy?.firstName || ''} ${currentTherapy?.lastName || ''}`.trim() ||
    '-';

  const hasCurrentTherapy =
    !!currentTherapy?._id && currentTherapy?.status !== 'past';
  const title = `Trenutna terapija: ${patientName}`;
  const medicationsLabel =
    Array.isArray(currentTherapy?.medications) &&
    currentTherapy.medications.length
      ? currentTherapy.medications.join(', ')
      : '';
  const internalNote =
    currentTherapy?.userInternalNote || currentTherapy?.internalNote;
  const canAddAcuteTherapy = !!currentTherapy?.userId;
  const reservation = currentTherapy?.reservation || null;
  const reservationActivation = currentTherapy?.reservationActivation || null;
  // Sopstvena nova rezervacija ima prioritet nad eventualno zastarelim
  // reservationActivation koji API jos uvek vraca iz prethodnog ciklusa.
  const pendingReservationActivation = !reservation
    ? reservationActivation
    : null;
  const reservationForViewing = reservation || reservationActivation;

  // Sidro za racunanje perioda rezervacije: poslednji mesec aktuelne terapije.
  const reservationAnchorDate = useMemo(() => {
    if (currentTherapy?.endDate) return currentTherapy.endDate;

    const sorted = sortOccurrences(occurrences);
    return sorted[sorted.length - 1]?.scheduledDate || null;
  }, [currentTherapy?.endDate, occurrences]);

  const highlightedActivity = useMemo(
    () => getHighlightedActivity(currentTherapy, occurrences),
    [currentTherapy, occurrences]
  );
  const highlightedOccurrenceId = highlightedActivity?.occurrenceId || null;
  const shouldShowRenewRow = highlightedActivity?.type === 'renew';
  const canUpdateTherapyTemplate =
    highlightedActivity?.type === 'occurrence' &&
    Number(highlightedActivity?.occurrence?.sequenceNumber) === 1;

  const displayedOccurrences = useMemo(() => {
    const sortedOccurrences = sortOccurrences(occurrences);

    if (!hasCurrentTherapy || !currentTherapy?.renew) {
      return sortedOccurrences;
    }

    const lastOccurrence = sortedOccurrences[sortedOccurrences.length - 1];

    return [
      ...sortedOccurrences,
      {
        _id: `renew-${currentTherapy?._id || 'therapy'}`,
        isRenewRow: true,
        sequenceNumber: Number(lastOccurrence?.sequenceNumber || 0) + 1,
        scheduledDate: currentTherapy.renew,
        status: 'renew',
        request: null,
        requestedDeliveryDate: null,
        userMessage: '',
        note: '',
      },
    ];
  }, [hasCurrentTherapy, occurrences, currentTherapy]);

  const getOccurrenceRowClass = useCallback(
    (row) => {
      const rowClasses = [];

      if (row?.status === 'saved') {
        rowClasses.push('occurrence-saved-row');
      }

      if (row?.isRenewRow) {
        if (shouldShowRenewRow) rowClasses.push('occurrence-current-row');
        return rowClasses.join(' ');
      }

      if (shouldShowRenewRow) {
        return rowClasses.join(' ');
      }

      if (row?._id === highlightedOccurrenceId) {
        rowClasses.push('occurrence-current-row');
      }

      return rowClasses.join(' ');
    },
    [highlightedOccurrenceId, shouldShowRenewRow]
  );

  const columnsHistory = useMemo(
    () => [
      {
        key: 'centralDate',
        label: 'Centralni datum',
        render: (v) => dayjs(v).format('DD.MM.YYYY'),
      },
      {
        key: 'status',
        label: 'Status',
        render: (_, row) =>
          row?.isAcute ? (
            <StatusFlag tone={STATUS_TONES.neutral}>Akutna terapija</StatusFlag>
          ) : getOccurrenceStatusDisplayLabel(row) ? (
            <StatusFlag tone={getTherapyStatusTone(row?.status)}>
              {getOccurrenceStatusDisplayLabel(row)}
            </StatusFlag>
          ) : (
            '-'
          ),
      },
      {
        key: 'medications',
        label: 'Lekovi',
        render: (v) => v.map((med) => med?.split(' ')[0]).join(', ') || '-',
      },
      {
        key: 'occurrences',
        label: 'Mesecne terapije',
        render: (v, row) => {
          const acuteDescription =
            row?.isAcute && row?.description?.trim()
              ? row.description.trim()
              : '';
          const hasOccurrences = Array.isArray(v) && v.length;

          if (!hasOccurrences && !acuteDescription) return '-';

          return (
            <div style={{ display: 'grid', gap: 4 }}>
              {hasOccurrences ? v.map((occurrence, index) => {
                const activityChangeDate =
                  occurrence?.activity?.changeDate ||
                  occurrence?.changeDate ||
                  occurrence?.lastUpdatedAt ||
                  occurrence?.updatedAt ||
                  occurrence?.scheduledDate;
                const date = activityChangeDate
                  ? dayjs(activityChangeDate).format('DD.MM.YYYY')
                  : '-';
                const sequenceNumber = occurrence?.sequenceNumber || index + 1;
                const status =
                  getOccurrenceStatusDisplayLabel(occurrence) || '-';
                const canEditOccurrence = occurrence?.status === 'saved';

                return (
                  <div
                    key={occurrence?._id || `${date}-${index}`}
                    style={historyOccurrenceRowStyle}
                  >
                    <span
                      style={
                        canEditOccurrence
                          ? historyOccurrenceSavedTextStyle
                          : undefined
                      }
                    >
                      {sequenceNumber}. {status} - {date}
                    </span>
                    <div style={historyOccurrenceActionsStyle}>
                      {canEditOccurrence && (
                        <button
                          type="button"
                          style={{
                            ...historyOccurrenceViewButtonStyle,
                            opacity:
                              modalActionLoading || !occurrence?._id ? 0.55 : 1,
                            cursor:
                              modalActionLoading || !occurrence?._id
                                ? 'not-allowed'
                                : 'pointer',
                          }}
                          disabled={modalActionLoading || !occurrence?._id}
                          aria-label="Izmeni mesečnu aktivnost"
                          title="Izmeni mesečnu aktivnost"
                          onClick={() => {
                            setSelectedOccurrence(occurrence);
                            setIsUpdateOpen(true);
                          }}
                        >
                          <EditOutlined aria-hidden="true" />
                        </button>
                      )}
                      <button
                        type="button"
                        style={{
                          ...historyOccurrenceViewButtonStyle,
                          opacity:
                            modalActionLoading || !occurrence?._id ? 0.55 : 1,
                          cursor:
                            modalActionLoading || !occurrence?._id
                              ? 'not-allowed'
                              : 'pointer',
                        }}
                        disabled={modalActionLoading || !occurrence?._id}
                        aria-label="Pregledaj istoriju izmena"
                        title="Pregledaj istoriju izmena"
                        onClick={() => {
                          setSelectedOccurrence(occurrence);
                          setIsLogsOpen(true);
                        }}
                      >
                        <EyeOutlined aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              }) : '-'}
              {acuteDescription && (
                <div style={historyAcuteDescriptionStyle}>
                  <span style={therapyInfoLabelStyle}>Napomena:</span>{' '}
                  {acuteDescription}
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [modalActionLoading]
  );

  const isModalActionBusy = modalActionLoading;

  return (
    <>
      <Modal
        className="user_therapies_modal"
        width={980}
        open={isOpen}
        onCancel={isModalActionBusy ? undefined : onClose}
        closable={!isModalActionBusy}
        mask={{ closable: !isModalActionBusy }}
        footer={null}
      >
        <Spin spinning={isModalActionBusy} description="Ucitavanje...">
          <div className="therapy-modal-title-row">
            <h3>{title}</h3>
          </div>
          <div style={therapyInfoStyle}>
            {hasCurrentTherapy && medicationsLabel && (
              <div>
                <span style={therapyInfoLabelStyle}>Lekovi:</span>{' '}
                {medicationsLabel}
              </div>
            )}
            <div>
              <span
                style={
                  internalNote
                    ? { ...therapyInfoLabelStyle, ...internalNoteInfoStyle }
                    : therapyInfoLabelStyle
                }
              >
                Interna napomena:
              </span>{' '}
              {internalNote ? (
                <span style={internalNoteInfoStyle}>{internalNote}</span>
              ) : (
                '-'
              )}
            </div>
            {hasCurrentTherapy && reservationForViewing && (
              <div>
                <span style={therapyInfoLabelStyle}>Rezervisane terapije:</span>{' '}
                <span style={reservationChipStyle}>
                  {reservationForViewing.label ||
                    `${reservationForViewing.count || ''} ${
                      reservationForViewing.medication || ''
                    }`.trim()}
                </span>
              </div>
            )}
          </div>
          <div style={actionRowStyle}>
            {!hasCurrentTherapy && (
              <button
                type="button"
                style={getActionButtonStyle(isModalActionBusy)}
                onClick={() => setIsAddNewOpen(true)}
                disabled={isModalActionBusy}
                aria-label="Dodaj terapiju"
                title="Dodaj terapiju"
              >
                <SwapOutlined aria-hidden="true" />
                <span>Dodaj terapiju</span>
              </button>
            )}
            {canAddAcuteTherapy && (
              <button
                type="button"
                style={getActionButtonStyle(isModalActionBusy)}
                onClick={() => setIsAddAcuteOpen(true)}
                disabled={isModalActionBusy}
                aria-label="Dodaj akutnu terapiju"
                title="Dodaj akutnu terapiju"
              >
                <PlusOutlined aria-hidden="true" />
                <span>Dodaj akutnu terapiju</span>
              </button>
            )}
            {hasCurrentTherapy && (
              <>
                <button
                  type="button"
                  style={getActionButtonStyle(isModalActionBusy)}
                  onClick={() => setIsRenewOpen(true)}
                  disabled={isModalActionBusy}
                  aria-label="Obnovi terapiju"
                  title="Obnovi terapiju"
                >
                  <ReloadOutlined aria-hidden="true" />
                  <span>Obnovi terapiju</span>
                </button>
                <button
                  type="button"
                  style={getActionButtonStyle(isModalActionBusy)}
                  onClick={() => setIsTerminateOpen(true)}
                  disabled={isModalActionBusy}
                  aria-label="Prekini terapiju i dodaj novu"
                  title="Prekini terapiju i dodaj novu"
                >
                  <SwapOutlined aria-hidden="true" />
                  <span>Prekini terapiju i dodaj novu</span>
                </button>
                <button
                  type="button"
                  style={getActionButtonStyle(
                    isModalActionBusy || !canUpdateTherapyTemplate
                  )}
                  onClick={() => setIsTemplateUpdateOpen(true)}
                  disabled={isModalActionBusy || !canUpdateTherapyTemplate}
                  aria-label="Promeni podatke o terapiji"
                  title={
                    canUpdateTherapyTemplate
                      ? 'Promeni podatke o terapiji'
                      : 'Promena podataka o terapiji je dozvoljena samo kada je prva aktivnost na redu'
                  }
                >
                  <img src="/assets/icons/edit-icon.svg" alt="" />
                  <span>Promeni podatke o terapiji</span>
                </button>
                {reservationForViewing ? (
                  <button
                    type="button"
                    style={getActionButtonStyle(isModalActionBusy)}
                    onClick={() => setIsViewReservationOpen(true)}
                    disabled={isModalActionBusy}
                    aria-label="Pogledaj rezervaciju unapred"
                    title="Pogledaj rezervaciju unapred"
                  >
                    <EyeOutlined aria-hidden="true" />
                    <span>Pogledaj rezervaciju unapred</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    style={getActionButtonStyle(isModalActionBusy)}
                    onClick={() => setIsReservationModalOpen(true)}
                    disabled={isModalActionBusy}
                    aria-label="Rezervisi terapiju unapred"
                    title="Rezervisi terapiju unapred"
                  >
                    <PlusOutlined aria-hidden="true" />
                    <span>Rezervisi terapiju unapred</span>
                  </button>
                )}
                {pendingReservationActivation && (
                  <>
                    <button
                      type="button"
                      style={getActionButtonStyle(isModalActionBusy)}
                      onClick={handleActivateReservation}
                      disabled={isModalActionBusy}
                      aria-label="Primeni rezervaciju"
                      title="Primeni rezervaciju"
                    >
                      <ReloadOutlined aria-hidden="true" />
                      <span>Primeni rezervaciju</span>
                    </button>
                    <Popconfirm
                      title="Brisanje rezervacije"
                      description="Da li ste sigurni da zelite obrisati rezervaciju?"
                      okText="Obrisi"
                      cancelText="Odustani"
                      okButtonProps={{ danger: true }}
                      disabled={isModalActionBusy}
                      onConfirm={handleDeleteReservation}
                    >
                      <button
                        type="button"
                        style={getActionButtonStyle(isModalActionBusy)}
                        disabled={isModalActionBusy}
                        aria-label="Obrisi rezervaciju"
                        title="Obrisi rezervaciju"
                      >
                        <DeleteOutlined aria-hidden="true" />
                        <span>Obrisi rezervaciju</span>
                      </button>
                    </Popconfirm>
                  </>
                )}
              </>
            )}
          </div>
          <Spin spinning={loading}>
            <GenericTable
              data={displayedOccurrences}
              columns={columns}
              wrapperClassName="therapy-modal-table-wrapper"
              rowClassName={getOccurrenceRowClass}
              actions={[
                {
                  key: 'change-status',
                  label: 'Promeni status',
                  icon: <img src="/assets/icons/edit-icon.svg" alt="Izmeni" />,
                  visible: (row) => !row?.isRenewRow,
                  disabled: () => isModalActionBusy,
                  onClick: (row) => {
                    setSelectedOccurrence(row);
                    setIsUpdateOpen(true);
                  },
                },
                {
                  key: 'request-status',
                  label: 'Promeni status zahteva',
                  icon: <img src="/assets/icons/edit-icon.svg" alt="Request" />,
                  visible: (row) => !row?.isRenewRow,
                  disabled: (row) => !row?.request?._id || isModalActionBusy,
                  onClick: (row) => {
                    setSelectedRequest(row.request || null);
                    setIsRequestUpdateOpen(true);
                  },
                },
                {
                  key: 'history',
                  label: 'Istorija izmena',
                  icon: <img src="/assets/icons/archive.svg" alt="History" />,
                  visible: (row) => !row?.isRenewRow,
                  disabled: () => isModalActionBusy,
                  onClick: (row) => {
                    setSelectedOccurrence(row);
                    setIsLogsOpen(true);
                  },
                },
              ]}
              emptyState={{
                icon: '/assets/icons/no-users.svg',
                title: hasCurrentTherapy
                  ? 'Trenutno nema instanci terapije'
                  : 'Nema trenutne terapije',
              }}
            />
          </Spin>
          <h3 style={{ marginTop: '20px' }}>Prosle terapije</h3>
          <Spin spinning={historyLoading}>
            <GenericTable
              data={historyTherapies}
              columns={columnsHistory}
              wrapperClassName="therapy-modal-table-wrapper"
              emptyState={{
                icon: '/assets/icons/no-users.svg',
                title: 'Nema istorije terapija',
              }}
            />
            <Pagination
              currentPage={historyCurrentPage}
              totalPages={historyTotalPages}
              totalResults={historyTotalCount}
              onPageChange={setHistoryCurrentPage}
              loading={historyLoading}
            />
          </Spin>
          {!!message && <span style={{ display: 'none' }}>{message}</span>}
        </Spin>
      </Modal>

      <UpdateOccurrenceStatusModal
        isOpen={isUpdateOpen}
        occurrence={selectedOccurrence}
        onClose={() => setIsUpdateOpen(false)}
        onUpdated={handleOccurrenceUpdated}
        onRefetchTherapies={onRefetchTherapies}
      />
      <UpdateTherapyOccurrenceRequestStatusModal
        isOpen={isRequestUpdateOpen}
        request={selectedRequest}
        onClose={() => setIsRequestUpdateOpen(false)}
        onUpdated={handleRequestUpdated}
      />
      <OccurrenceStatusLogsModal
        isOpen={isLogsOpen}
        occurrence={selectedOccurrence}
        onClose={handleLogsClose}
      />
      <UpdateTherapyTemplateModal
        isOpen={isTemplateUpdateOpen}
        therapy={currentTherapy}
        occurrences={occurrences}
        onClose={() => setIsTemplateUpdateOpen(false)}
        onUpdated={handleTemplateUpdated}
      />
      <RenewTherapyModal
        isOpen={isRenewOpen}
        therapy={currentTherapy}
        onClose={() => setIsRenewOpen(false)}
        onRenewed={handleTherapyRenewed}
        onLoadingChange={setModalActionLoading}
      />
      <TerminateTherapyModal
        isOpen={isTerminateOpen}
        therapy={currentTherapy}
        onClose={() => setIsTerminateOpen(false)}
        onTerminated={handleTherapyTerminated}
        onLoadingChange={setModalActionLoading}
      />
      <AddTherapyModal
        isOpen={isAddNewOpen}
        user={getTherapyUser(currentTherapy)}
        onClose={() => setIsAddNewOpen(false)}
        onCreate={handleNewTherapyCreated}
      />
      <AddTherapyModal
        isOpen={isAddAcuteOpen}
        user={getTherapyUser(currentTherapy)}
        onClose={() => setIsAddAcuteOpen(false)}
        onCreate={handleAcuteTherapyCreated}
        defaultIsAcute
        lockIsAcute
        defaultDurationMonths="1"
        lockDurationMonths
      />
      <TherapyReservationModal
        isOpen={isReservationModalOpen}
        therapy={currentTherapy}
        reservation={reservation}
        onClose={() => setIsReservationModalOpen(false)}
        onSaved={handleReservationChanged}
        onLoadingChange={setModalActionLoading}
      />
      <ViewTherapyReservationModal
        isOpen={isViewReservationOpen}
        therapy={currentTherapy}
        reservation={reservationForViewing}
        anchorDate={reservationAnchorDate}
        onClose={() => setIsViewReservationOpen(false)}
        onEdit={
          reservation
            ? () => {
                setIsViewReservationOpen(false);
                setIsReservationModalOpen(true);
              }
            : undefined
        }
        onDeleted={reservation ? handleReservationChanged : undefined}
      />
    </>
  );
};

TherapyOccurrencesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  onRefetchTherapies: PropTypes.func,
  onReloadTherapyContext: PropTypes.func,
};

export default TherapyOccurrencesModal;
